/**
 * Connected storage on the Clarid Agent page: the bank's SharePoint or OneDrive folder that Ask Clarid can
 * read from. Hidden until Clarid's Microsoft connector is set up. An Owner or Compliance Officer signs in
 * with Microsoft, then pastes the link to ONE folder; Clarid reads only inside it, and every read is recorded.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CloudIcon, LinkIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { agentApi, type ConnectorStatus } from '../../lib/agent';
import { primaryBtn, secondaryBtn } from './cardKit';

/** The outcome Microsoft's redirect carried back (?connector=microsoft&result=…), read once. */
function useRedirectResult() {
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('connector') !== 'microsoft') return;
    setResult(q.get('result') === 'connected'
      ? { ok: true, message: 'Microsoft is connected. Now paste the link to the folder Clarid may read.' }
      : { ok: false, message: q.get('message') ?? 'The Microsoft sign-in did not finish.' });
    ['connector', 'result', 'message'].forEach((k) => q.delete(k));
    const rest = q.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
  }, []);
  return result;
}

export default function ConnectedStorage({ teamId }: { teamId: string }) {
  const qc = useQueryClient();
  const redirect = useRedirectResult();
  const [link, setLink] = useState('');
  const key = ['agent-connectors', teamId];
  const { data } = useQuery({ queryKey: key, queryFn: () => agentApi.connectors(teamId).then((r) => r.data), staleTime: 60_000 });
  const setStatus = (s: ConnectorStatus) => qc.setQueryData(key, s);

  const start = useMutation({
    mutationFn: () => agentApi.startMicrosoft(teamId).then((r) => r.data.url),
    onSuccess: (url) => { window.location.href = url; },
  });
  const folder = useMutation({
    mutationFn: () => agentApi.setMicrosoftFolder(teamId, link.trim()).then((r) => r.data),
    onSuccess: (s) => { setLink(''); setStatus(s); },
  });
  const remove = useMutation({ mutationFn: () => agentApi.disconnectMicrosoft(teamId).then((r) => r.data), onSuccess: setStatus });

  const ms = data?.microsoft;
  if (!ms?.configured) return null;
  const c = ms.connection;

  return (
    <div className="rounded-lg border border-gray-200 px-4 py-3 text-left text-sm">
      <p className="mb-1 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">
        <CloudIcon className="h-4 w-4" /> Connected storage
      </p>
      {redirect && (
        <p className={`mb-2 inline-flex items-start gap-1.5 text-xs ${redirect.ok ? 'text-emerald-700' : 'text-red-700'}`}>
          {redirect.ok ? <CheckCircleIcon className="h-4 w-4 shrink-0" /> : <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />} {redirect.message}
        </p>
      )}

      {!c && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-600">Let Ask Clarid read files from one SharePoint or OneDrive folder. Clarid reads only that folder, read-only, and records every file it reads.</p>
          <button type="button" className={primaryBtn} disabled={start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? 'Opening Microsoft…' : 'Connect SharePoint or OneDrive'}
          </button>
        </div>
      )}

      {c?.status === 'PENDING_FOLDER' && (
        <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (link.trim()) folder.mutate(); }}>
          <p className="w-full text-xs text-gray-600">Signed in as {c.accountName ?? 'your Microsoft account'}. Paste the link to the folder Clarid may read (in SharePoint: the folder’s ⋯ menu → Copy link).</p>
          <input
            aria-label="Folder link"
            className="min-w-[18rem] flex-1 rounded border border-gray-300 px-2 py-1 text-xs"
            placeholder="https://yourbank.sharepoint.com/…"
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
          <button type="submit" className={primaryBtn} disabled={folder.isPending || !link.trim()}>
            <LinkIcon className="h-4 w-4" /> {folder.isPending ? 'Checking…' : 'Use this folder'}
          </button>
          <button type="button" className={secondaryBtn} disabled={remove.isPending} onClick={() => remove.mutate()}>Cancel</button>
        </form>
      )}

      {(c?.status === 'ACTIVE' || c?.status === 'ERROR') && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-700">
            {c.status === 'ACTIVE' ? <CheckCircleIcon className="mr-1 inline h-4 w-4 text-emerald-600" /> : <ExclamationTriangleIcon className="mr-1 inline h-4 w-4 text-amber-600" />}
            {c.folderWebUrl ? <a className="font-medium text-ocean-700 hover:underline" href={c.folderWebUrl} target="_blank" rel="noreferrer">{c.folderName}</a> : <span className="font-medium">{c.folderName}</span>}
            {' '}· connected by {c.accountName ?? 'a Microsoft account'}
            {c.status === 'ERROR' && <span className="block text-amber-700">{c.lastError ?? 'The connection needs attention.'} Disconnect and connect again.</span>}
          </p>
          <button type="button" className={secondaryBtn} disabled={remove.isPending} onClick={() => remove.mutate()}>Disconnect</button>
        </div>
      )}
    </div>
  );
}
